import {
  CheckCircle2,
  Play,
  AlertCircle,
  Dumbbell,
  Clock,
  Repeat,
  LoaderCircle,
} from "lucide-react";
import { usePublicRecoveryPlan } from "../hooks/usePublicRecoveryPlan";
import { useParams } from "react-router-dom";

// Helper para transformar links de YouTube a URLs incrustables
const getYouTubeEmbedUrl = (url: string | null): string | null => {
  if (!url) return null;

  // Si ya es una URL de embed, la dejamos tal cual
  if (url.includes("youtube.com/embed/")) return url;

  // Caso 1: YouTube Shorts (youtube.com/shorts/VIDEO_ID)
  const shortMatch = url.match(/(?:youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
  if (shortMatch && shortMatch[1]) {
    return `https://www.youtube.com/embed/${shortMatch[1]}?rel=0&modestbranding=1`;
  }

  // Caso 2: Vídeo Estándar (youtube.com/watch?v=VIDEO_ID o youtu.be/VIDEO_ID)
  const videoMatch = url.match(
    /(?:v=|\/v\/|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/,
  );
  if (videoMatch && videoMatch[1]) {
    return `https://www.youtube.com/embed/${videoMatch[1]}?rel=0&modestbranding=1`;
  }

  return null;
};

export default function PatientRecoveryPlanPage() {
  // 1. Capturamos el planId de la URL
  const { planId } = useParams<{ planId: string }>();

  // 2. Obtenemos los datos limpios desde el endpoint
  const {
    data: plan,
    isLoading,
    isError,
  } = usePublicRecoveryPlan(planId || "");

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoaderCircle className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError || !plan) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background text-center">
        <AlertCircle className="text-destructive w-12 h-12 mb-4" />
        <h1 className="text-xl font-bold">Plan no encontrado</h1>
        <p className="text-muted-foreground text-sm max-w-xs mt-2">
          El enlace no es válido o el plan de recuperación ha concluido.
          Contacta con tu clínica.
        </p>
      </div>
    );
  }

  const { clinic, patientName, treatmentNotes, exercises } = plan;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans antialiased pb-12">
      {/* 🟢 HEADER: Branding Dinámico de la Clínica */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {clinic.branding?.logoUrl ? (
            <div className="flex items-center gap-2">
              <img
                src={clinic.branding.logoUrl}
                alt={clinic.name}
                className="h-8 w-auto object-contain"
              />
              {clinic.name}
            </div>
          ) : (
            <span className="font-bold text-lg tracking-tight text-primary">
              {clinic.name}
            </span>
          )}
        </div>
        <span className="text-xs bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full font-medium flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />{" "}
          Activo
        </span>
      </header>

      <main className="max-w-md mx-auto px-4 pt-6 space-y-6">
        {/* 👤 BIENVENIDA Y NOTA DE LA IA/FISIO */}
        <section className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 space-y-3">
          <h1 className="text-xl font-bold tracking-tight text-slate-800">
            ¡Hola, {patientName}! 👋
          </h1>
          <p className="text-xs text-slate-500 leading-normal">
            Este es tu plan de recuperación personalizado. Sigue las
            indicaciones en el orden establecido.
          </p>

          {treatmentNotes && (
            <div className="mt-2 p-3 bg-primary/5 rounded-xl border border-primary/10 text-xs text-slate-700 leading-relaxed italic">
              "{treatmentNotes}"
            </div>
          )}
        </section>

        {/* 🏋️‍♂️ LISTADO DE EJERCICIOS (PASO A PASO RIGUROSO) */}
        <section className="space-y-4">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400 flex items-center gap-2">
            <Dumbbell size={16} /> Tu rutina de ejercicios
          </h2>

          {exercises.map((item, index) => (
            <article
              key={item.id}
              className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 flex flex-col"
            >
              {/* Encabezado del ejercicio */}
              <div className="p-4 flex items-start justify-between border-b border-slate-50">
                <div className="space-y-1">
                  <span className="text-[10px] font-bold text-primary uppercase tracking-widest block">
                    Paso {index + 1} de {exercises.length}
                  </span>
                  <h3 className="font-bold text-base text-slate-800">
                    {item.title}
                  </h3>
                  <p className="text-xs text-slate-500 line-clamp-2">
                    {item.description}
                  </p>
                </div>
              </div>

              {/* Contenedor del Vídeo / GIF */}
              <div className="relative aspect-video w-full bg-slate-900 flex items-center justify-center overflow-hidden">
                {item.videoUrl ? (
                  (() => {
                    const embedUrl = getYouTubeEmbedUrl(item.videoUrl);

                    if (embedUrl) {
                      // 🎯 Si detectamos que es YouTube, usamos un Iframe optimizado
                      return (
                        <iframe
                          src={embedUrl}
                          title={item.title}
                          className="w-full h-full border-0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      );
                    } else {
                      // 💾 Si es un archivo físico (.mp4 de Supabase/Cloudinary), usamos video HTML5
                      return (
                        <video
                          src={item.videoUrl}
                          controls
                          className="w-full h-full object-cover"
                          poster="/video-placeholder.png"
                        />
                      );
                    }
                  })()
                ) : (
                  <div className="flex flex-col items-center text-slate-500 gap-2 p-6 text-center">
                    <Play className="w-8 h-8 opacity-40" />
                    <span className="text-xs">Visualización de movimiento</span>
                  </div>
                )}
              </div>

              {/* Grid de Métricas (Mascado para el paciente) */}
              <div className="grid grid-cols-3 bg-slate-50/50 border-b border-slate-50 text-center py-3">
                <div className="flex flex-col items-center justify-center border-r border-slate-100">
                  <Repeat size={14} className="text-slate-400 mb-1" />
                  <span className="text-xs font-bold text-slate-800">
                    {item.metrics.series} Series
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center border-r border-slate-100">
                  <Dumbbell size={14} className="text-slate-400 mb-1" />
                  <span className="text-xs font-bold text-slate-800">
                    {item.metrics.repetitions}
                  </span>
                </div>
                <div className="flex flex-col items-center justify-center">
                  <Clock size={14} className="text-slate-400 mb-1" />
                  <span className="text-xs font-bold text-slate-800">
                    {item.metrics.restSeconds}s Descanso
                  </span>
                </div>
              </div>

              {/* Notas específicas del ejercicio */}
              {item.notes && (
                <div className="p-3 bg-amber-50/40 text-[11px] text-amber-800 border-t border-amber-100/50 flex gap-2">
                  <CheckCircle2
                    size={14}
                    className="text-amber-600 shrink-0 mt-0.5"
                  />
                  <p>
                    <strong>Nota del fisio:</strong> {item.notes}
                  </p>
                </div>
              )}
            </article>
          ))}
        </section>
      </main>
    </div>
  );
}
